import { Directionality } from '@angular/cdk/bidi';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  inject,
  OnDestroy,
  Pipe,
  PipeTransform,
  Renderer2,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DropdownPosition, NgDropdownPanelComponent, NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { CookieService } from 'ngx-cookie-service';
import { map, startWith } from 'rxjs/operators';

import { Position, WINDOW } from '@ideals/types';
import { AutofocusDirective } from '@ideals/ui-components/autofocus';
import { SkeletonComponent } from '@ideals/ui-components/skeleton';
import { TooltipModule } from '@ideals/ui-components/tooltip';
import { getDomain } from '@ideals/utils';

import { HelpCountry } from '../../models/classes';
import { COUNTRY_CODE_COOKIE } from '../../models/constants';
import { IHelpCountry } from '../../models/interfaces';
import { HelpStore } from '../../store/help.store';

export const COUNTRIES_GROUP_BY_FIELD = 'regionName';
const DROPDOWN_PANEL_SELECTOR = '.ng-dropdown-panel';
const SCROLL_HOST_SELECTOR = '.scroll-host';
const SCROLLED_CLASS = 'scrolled';

@Pipe({
  name: 'idealsSplit',
  standalone: true,
})
export class SplitPipe implements PipeTransform {
  transform(value: string): string[] {
    return value?.split(/\n/) ?? [];
  }
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    AsyncPipe,
    AutofocusDirective,
    FormsModule,
    NgClass,
    NgSelectModule,
    SkeletonComponent,
    SplitPipe,
    TooltipModule,
  ],
  selector: 'ideals-help-contacts',
  styleUrls: ['./help-contacts.component.scss'],
  templateUrl: './help-contacts.component.html',
})
export class HelpContactsComponent implements OnDestroy {
  protected readonly countriesSelect = viewChild<NgSelectComponent>('countriesSelect');

  readonly #breakpointObserver = inject(BreakpointObserver);
  readonly #cookieService = inject(CookieService);
  readonly #directionality = inject(Directionality);
  readonly #document = inject(DOCUMENT);
  readonly #helpStore = inject(HelpStore);
  readonly #renderer = inject(Renderer2);
  readonly #window = inject(WINDOW);

  readonly #adjustDropdownPosition = (): void => {
    (this.countriesSelect()?.dropdownPanel() as NgDropdownPanelComponent).adjustPosition();
  };
  readonly #eventScrollOptions = { capture: true, passive: true };
  readonly #hideDropdown = (): void => {
    this.countriesSelect()?.close();
  };

  protected readonly countries = this.#helpStore.countries;
  protected readonly loading = this.#helpStore.countriesLoading;
  protected readonly selectedCountry = this.#helpStore.selectedCountry;

  protected readonly dropdownPosition$ = this.#breakpointObserver.observe('(min-width: 1280px)')
    .pipe(
      map((state: BreakpointState): DropdownPosition => state.matches ? 'bottom' : 'auto'),
      startWith<DropdownPosition>('auto'),
    );

  protected readonly countriesGroupByField = COUNTRIES_GROUP_BY_FIELD;

  ngOnDestroy(): void {
    this.#removeWindowListeners();
  }

  onSelectClose(): void {
    this.#removeWindowListeners();
  }

  protected onSelectOpen(): void {
    if (this.countriesSelect()) {
      setTimeout(() => {
        const maxElementHeight = 269;
        const panel = this.#document.querySelector<HTMLElement>(DROPDOWN_PANEL_SELECTOR);
        const nativeElement = panel?.querySelector<HTMLElement>(SCROLL_HOST_SELECTOR);

        if (!panel || !nativeElement) {
          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        if (this.countriesSelect().currentPanelPosition === Position.Bottom) {
          const dropdownTop = nativeElement.getBoundingClientRect().top;
          const bottomMargin = 5;
          const heightToBottom = this.#window.innerHeight - dropdownTop - bottomMargin;
          const maxHeight = Math.min(maxElementHeight, heightToBottom);

          nativeElement.style.setProperty('max-height', `${maxHeight}px`, 'important');
        } else {
          nativeElement.style.setProperty('max-height', `${maxElementHeight}px`, 'important');
        }

        nativeElement.scrollTop = 0;
        this.#addWindowListeners();

        if (this.#directionality.value === 'rtl') {
          const select = this.countriesSelect().element.getBoundingClientRect();
          const parent = panel.offsetParent?.getBoundingClientRect() ?? { right: this.#window.innerWidth };

          panel.style.right = `${parent.right - select.right}px`;
        }

        this.#renderer.addClass(panel, SCROLLED_CLASS);
      }, 0);
    }
  }

  protected searchCountry(term: string, { countryName, phoneCode }: IHelpCountry): boolean {
    const query = term.toLocaleLowerCase();
    const value = `${countryName}${phoneCode}`.toLocaleLowerCase();

    return value.includes(query);
  }

  protected selectCountry(selectedCountry: IHelpCountry): void {
    if (selectedCountry instanceof HelpCountry) {
      this.#cookieService.set(COUNTRY_CODE_COOKIE, selectedCountry.countryCode, undefined, '/', getDomain(), true);
      this.#helpStore.selectCountry(selectedCountry);
    }
  }

  #addWindowListeners(): void {
    this.#window.addEventListener('scroll', this.#adjustDropdownPosition, this.#eventScrollOptions);
    // adjustPosition does not update X axis
    this.#window.addEventListener('orientationchange', this.#hideDropdown);
  }

  #removeWindowListeners(): void {
    this.#window.removeEventListener('scroll', this.#adjustDropdownPosition, this.#eventScrollOptions);
    this.#window.removeEventListener('orientationchange', this.#hideDropdown);
  }
}
